import React, { memo } from 'react'
import { Select, ISelectOption } from 'components/forms/Selects'
import {
  RULE_TYPE_OPTIONS,
  APPOINTMENT_TYPE,
} from 'apps/spot-quote-control-panel/constants/spotBiddingConfig'
import {
  ISpotBiddingRule,
  PARENT_EQUIPMENT_KEY,
  RULE_ACTION,
} from 'apps/spot-quote-control-panel/types/spotBiddingConfig'

interface IRuleTypeProps {
  value: RecursivePartial<ISpotBiddingRule>
  isGlobal: boolean
  onChange(change: RecursivePartial<ISpotBiddingRule>): void
}

export const RuleType = memo<IRuleTypeProps>(({ isGlobal, onChange, value }) => {
  const { ruleAction } = value
  const {
    appointmentTimeCriteria,
    carrierWaitTimeCriteria,
    contractCriteria,
    countLoadsQuotedCriteria,
    dateCriteria,
    distanceToMarketCenterCriteria,
    geographicCriteria,
    isBaseline,
    isDrop,
    isMultiStop,
    isSameDay,
    isWeekend,
    laneGeographicCriteria,
    mileageCriteria,
    parentEquipmentKey,
    proximalHourCriteria,
    stopSpecificDropCriteria,
    transitDifferenceCriteria,
  } = value
  const { isInbound } = geographicCriteria || {}
  const { pickupRangeStart } = appointmentTimeCriteria || {}

  const ruleTypeHandler = (e: ISelectOption) => {
    const criteriaChange: RecursivePartial<ISpotBiddingRule> = {
      isBaseline: false,
      isSameDay: false,
      isWeekend: false,
      isDrop: false,
      isMultiStop: false,
      parentEquipmentKey: undefined,
      geographicCriteria: undefined,
      distanceToMarketCenterCriteria: undefined,
      laneGeographicCriteria: undefined,
      contractCriteria: undefined,
      countLoadsQuotedCriteria: undefined,
      mileageCriteria: undefined,
      proximalHourCriteria: undefined,
      appointmentTimeCriteria: undefined,
      carrierWaitTimeCriteria: undefined,
      dateCriteria: undefined,
      stopSpecificDropCriteria: undefined,
      transitDifferenceCriteria: undefined,
    }
    if (e.value === 'baseline') {
      criteriaChange.isBaseline = true
    } else if (e.value === 'outbound') {
      criteriaChange.geographicCriteria = { ...geographicCriteria, isInbound: false }
    } else if (e.value === 'inbound') {
      criteriaChange.geographicCriteria = { ...geographicCriteria, isInbound: true }
    } else if (e.value === 'lane') {
      criteriaChange.laneGeographicCriteria = { ...laneGeographicCriteria }
    } else if (e.value === 'contract') {
      criteriaChange.contractCriteria = { ...contractCriteria }
    } else if (e.value === 'count') {
      criteriaChange.countLoadsQuotedCriteria = { ...countLoadsQuotedCriteria }
    } else if (e.value === 'mileage') {
      criteriaChange.mileageCriteria = { ...mileageCriteria }
    } else if (e.value === 'transitDifference') {
      criteriaChange.transitDifferenceCriteria = { ...transitDifferenceCriteria }
    } else if (e.value === 'distance') {
      criteriaChange.distanceToMarketCenterCriteria = { ...distanceToMarketCenterCriteria }
    } else if (e.value === 'date') {
      criteriaChange.isSameDay = true
    } else if (e.value === 'equipment') {
      criteriaChange.parentEquipmentKey = PARENT_EQUIPMENT_KEY.dryVan
    } else if (e.value === 'appointmentPickup' || e.value === 'appointmentDelivery') {
      const [appointmentTypeToSet, appointmentTypeToClear] =
        e.value === 'appointmentPickup'
          ? [APPOINTMENT_TYPE.PICKUP, APPOINTMENT_TYPE.DELIVERY]
          : [APPOINTMENT_TYPE.DELIVERY, APPOINTMENT_TYPE.PICKUP]

      criteriaChange.mileageCriteria = { ...mileageCriteria }
      criteriaChange.appointmentTimeCriteria = {
        ...appointmentTimeCriteria,
        [`${appointmentTypeToSet}RangeStart`]: '00:00',
        [`${appointmentTypeToSet}RangeEnd`]: '00:00',
        [`${appointmentTypeToClear}RangeStart`]: undefined,
        [`${appointmentTypeToClear}RangeEnd`]: undefined,
      }
    } else if (e.value === 'transit') {
      criteriaChange.carrierWaitTimeCriteria = { ...carrierWaitTimeCriteria }
    } else if (e.value === 'isDrop') {
      criteriaChange.isDrop = true
    } else if (e.value === 'isMultiStop') {
      criteriaChange.isMultiStop = true
    } else if (e.value === 'stopSpecificDrop') {
      criteriaChange.stopSpecificDropCriteria = {
        ...stopSpecificDropCriteria,
      }
      criteriaChange.stopSpecificDropCriteria = stopSpecificDropCriteria || {
        pickupDropTrailer: false,
        deliveryDropTrailer: false,
      }
    }
    onChange(criteriaChange)
  }

  let ruleType = isBaseline ? 'baseline' : ''
  if (geographicCriteria) {
    ruleType = isInbound ? 'inbound' : 'outbound'
  } else if (laneGeographicCriteria) {
    ruleType = 'lane'
  } else if (contractCriteria) {
    ruleType = 'contract'
  } else if (countLoadsQuotedCriteria) {
    ruleType = 'count'
  } else if (mileageCriteria && !appointmentTimeCriteria) {
    ruleType = 'mileage'
  } else if (distanceToMarketCenterCriteria) {
    ruleType = 'distance'
  } else if (isSameDay || isWeekend || dateCriteria || proximalHourCriteria) {
    ruleType = 'date'
  } else if (parentEquipmentKey) {
    ruleType = 'equipment'
  } else if (appointmentTimeCriteria) {
    ruleType = pickupRangeStart ? 'appointmentPickup' : 'appointmentDelivery'
  } else if (carrierWaitTimeCriteria) {
    ruleType = 'transit'
  } else if (transitDifferenceCriteria) {
    ruleType = 'transitDifference'
  } else if (isDrop) {
    ruleType = 'isDrop'
  } else if (isMultiStop) {
    ruleType = 'isMultiStop'
  } else if (stopSpecificDropCriteria) {
    ruleType = 'stopSpecificDrop'
  }

  const permittedRuleTypeOptions = (() => {
    let permittedOptions = RULE_TYPE_OPTIONS
    if (!isGlobal) {
      // Distance to market center is only permitted as a criterion
      // for global rules:
      permittedOptions = permittedOptions.filter(option => option.value !== 'distance')
    }
    if (ruleAction === RULE_ACTION.onlyBidOn) {
      permittedOptions = permittedOptions.filter(option => {
        return option.value === 'outbound' || option.value === 'inbound' || option.value === 'lane'
      })
    }
    return permittedOptions
  })()

  const placeholder =
    ruleAction === RULE_ACTION.onlyBidOn ? 'Loads this rule should apply to' : 'Choose an option'

  return (
    <div className='col-md-6 col-xs-4'>
      <Select
        label='Rule Option'
        placeholder={placeholder}
        options={permittedRuleTypeOptions}
        value={ruleType}
        onChange={ruleTypeHandler}
        dataTest='modal-rule-type'
        required
        large
      />
    </div>
  )
})
